import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import { sec } from "../../src/app/security";
import { setCredentials, logOut } from "../../src/features/auth/auth0Slice";
import queryString from "query-string";
import { openAlert } from "../../src/features/settingsSlice";
import { RootState } from "../../src/app/store";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.VITE_ORDER_SERVER_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    const idToken = (getState() as RootState).auth.idToken;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
      headers.set("id-token", idToken);
    }
    headers.set("requesting-app", "web");
    return headers;
  },
  paramsSerializer: (params: Record<string, unknown>) =>
    queryString.stringify(params, { arrayFormat: "none" }),
});

const baseQueryWithReauth = retry(async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  // Backend returns 403 if token is expired
  if (result?.error?.status === 401 || result?.error?.status === 403) {
    //send refresh token to backend
    const accessToken = await sec.getAccessTokenSilently();
    if (accessToken) {
      const user = (api.getState() as RootState).auth.user;
      const idToken = (api.getState() as RootState).auth.idToken;
      const roles = (api.getState() as RootState).auth.roles;
      const tenants = (api.getState() as RootState).auth.tenants;
      const credentials = {
        user: user,
        token: accessToken,
        idToken: idToken,
        roles: roles,
        tenants: tenants,
      };
      api.dispatch(setCredentials(credentials));
      //retry original request with new access token
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logOut());
    }
  } else if (result?.error?.status === 406) {
    // RegionException
    window.location.replace("/error");
  } else if (
    ["FETCH_ERROR", "PARSING_ERROR", 550, "550", 500, "500"].includes(
      result?.error?.status || "",
    )
  ) {
    const retryEndpoints = ["webApiV1ListSites", "webApiV1ListOars"];
    if (retryEndpoints.includes(api.endpoint)) {
      // return without dispatching alert
      // this also triggers a retry
      return result;
    }
    api.dispatch(openAlert());
  }
  if (result.error) {
    // this force-fails the query so it doesn't get retried
    retry.fail(result.error, result.meta);
  }
  return result;
});

export const api = createApi({
  endpoints: () => ({}),
  baseQuery: baseQueryWithReauth,
  reducerPath: "orderAPI",
});
